import { Capacitor } from '@capacitor/core';
import { Geolocation, type PermissionStatus } from '@capacitor/geolocation';

export const LOCATION_DENIED_MESSAGE =
  'Location permission denied. Enable location in Settings to record flights.';

export const LOCATION_SERVICES_DISABLED_MESSAGE =
  'Turn on location services on your device to record flights.';

export const BACKGROUND_LOCATION_DENIED_MESSAGE =
  'Allow "All the time" location access so we can record your flight when the screen is off or app is in the background.';

export function isForegroundLocationGranted(status: PermissionStatus): boolean {
  return status.location === 'granted' || status.coarseLocation === 'granted';
}

export function isLocationPermissionError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('permission')
    || lower.includes('denied')
    || lower.includes('not authorized')
    || lower.includes('all the time')
    || lower.includes('location access')
    || lower.includes('location services')
    || lower.includes('enable location')
    || lower.includes('enable gps')
  );
}

function isLocationServicesDisabledError(message: string): boolean {
  const lower = message.toLowerCase();
  return lower.includes('location service') || lower.includes('location provider');
}

export async function ensureForegroundLocationPermission(): Promise<{
  granted: boolean;
  error?: string;
}> {
  if (Capacitor.getPlatform() === 'web') {
    return { granted: true };
  }

  try {
    const permission = await Geolocation.requestPermissions();
    if (isForegroundLocationGranted(permission)) {
      return { granted: true };
    }
    return { granted: false, error: LOCATION_DENIED_MESSAGE };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    if (isLocationServicesDisabledError(message)) {
      return { granted: false, error: LOCATION_SERVICES_DISABLED_MESSAGE };
    }
    return { granted: false, error: LOCATION_DENIED_MESSAGE };
  }
}
